import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * Close a tab's overlays as soon as the user leaves that tab (KP-2453).
 *
 * antd `Tabs` keeps an inactive pane mounted while antd `Modal` renders through a portal on
 * `<body>`, so hiding the pane does not hide a modal opened inside it — it keeps floating over
 * whichever tab is opened next. This covers browser Back too, which only changes `?type=`.
 *
 * Only needed where the tab's content stays mounted. A tab whose sub-views are already gated on
 * `searchParams.get("type")` (Billing, for one) unmounts them and takes its modals with it.
 *
 * @param tabKey the `?type=` value this component belongs to
 * @param close  closes every overlay owned here; may be an inline arrow, it is read through a ref
 */
const useCloseOnTabLeave = (tabKey: string, close: () => void) => {
  const [searchParams] = useSearchParams();
  const activeTab = searchParams.get("type");

  // Held in a ref so an inline arrow does not re-run the effect on every render.
  const closeRef = useRef(close);
  closeRef.current = close;

  useEffect(() => {
    if (activeTab === tabKey) return;
    closeRef.current();
  }, [activeTab, tabKey]);
};

export default useCloseOnTabLeave;
